import { motion } from "framer-motion";
import { TrainFront, Landmark, Route } from "lucide-react";

const MILESTONES = [
  { icon: Route, place: "Noida Expressway", time: "Direct", note: "Three-side-open plot on the corridor" },
  { icon: Route, place: "FNG Expressway", time: "Direct", note: "Faridabad–Noida–Ghaziabad connectivity" },
  { icon: TrainFront, place: "Two Major Metro Stations", time: "Nearby", note: "Easy access to the Aqua Line network" },
  { icon: Landmark, place: "Noida's Corporate Corridor", time: "Minutes", note: "Surrounded by IT/ITES campuses and universities" },
];

export default function Location() {
  return (
    <section id="location" data-testid="location-section" className="py-28 sm:py-36 bg-[#0D0F12]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Location &amp; Connectivity</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6]">
            Plot 1A, Sector 140. <span className="gold-text italic">The Eye of Noida.</span>
          </h2>
          <p className="mt-5 max-w-md text-sm sm:text-base text-[#9CA3AF] leading-relaxed">
            Directly on the Noida Expressway with FNG connectivity and two major metro stations in easy reach —
            exactly where Noida&apos;s commercial gravity is concentrating.
          </p>

          <div className="mt-10 space-y-3" data-testid="location-milestones">
            {MILESTONES.map((m, i) => (
              <motion.div
                key={m.place}
                data-testid={`milestone-${i}`}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-5 rounded-2xl border border-white/[0.07] bg-[#121418] p-5 hover:border-[#D4AF37]/40 transition-colors duration-500"
              >
                <div className="w-11 h-11 shrink-0 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center">
                  <m.icon size={18} className="text-[#D4AF37]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-sans font-medium text-[#F3F4F6] text-sm">{m.place}</div>
                  <div className="text-xs text-[#9CA3AF] mt-0.5">{m.note}</div>
                </div>
                <span className="shrink-0 px-3.5 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 font-mono text-xs text-[#F3E5AB]">
                  {m.time}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          data-testid="location-visual"
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative rounded-3xl overflow-hidden border border-white/[0.07] min-h-[420px] lg:min-h-[560px]"
        >
          <iframe
            title="EON Fairfox location map — GC68+GQ Noida"
            data-testid="location-map"
            src="https://www.google.com/maps?q=GC68%2BGQ%20Noida%2C%20Uttar%20Pradesh&z=15&output=embed"
            loading="lazy"
            className="absolute inset-0 w-full h-full border-0 [filter:invert(0.9)_hue-rotate(180deg)_grayscale(0.25)_contrast(0.95)]"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E]/90 via-transparent to-[#0B0C0E]/20 pointer-events-none" />
          <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/10 bg-[#0B0C0E]/70 backdrop-blur-xl p-5 flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Project Address · Plus Code GC68+GQ</div>
              <div className="mt-1 font-display text-lg font-semibold text-[#F3F4F6]">Plot 1A, Sector 140, Noida, UP</div>
            </div>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-60" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#D4AF37]" />
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
